import React, { useState, useRef, useEffect } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { apiRequest } from '../utils/apiClient';
import { useTheme } from '../hooks/useTheme';

const UserSummary = ({ user }) => (
  <div className="login-user">
    {user.picture && (
      <img src={user.picture} alt="" className="login-avatar" referrerPolicy="no-referrer" />
    )}
    <div>
      <strong>{user.name || user.email}</strong>
      <p className="login-email">{user.email}</p>
    </div>
  </div>
);

// Sign-in: Google account first, then an authenticator code. Both are
// verified by the backend; this component only collects the inputs.
export const UserLogin = ({ onAuthSuccess, notice }) => {
  const [step, setStep] = useState('google'); // 'google', 'qr', or '2fa_verify'
  const [googleUser, setGoogleUser] = useState(null);
  const [enrollment, setEnrollment] = useState(null); // { secret, qrCodeDataUrl } on first sign-in
  const [twoFACodeInput, setTwoFACodeInput] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const codeInputRef = useRef(null);
  const theme = useTheme();

  const googleClientId =
    (typeof window !== 'undefined' &&
      window.ENV_VARS &&
      window.ENV_VARS.REACT_APP_GOOGLE_CLIENT_ID) ||
    process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (step === '2fa_verify' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  const resetToGoogle = (message = '') => {
    setGoogleUser(null);
    setEnrollment(null);
    setTwoFACodeInput('');
    setError(message);
    setStep('google');
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const result = await apiRequest('/api/auth/google', {
        method: 'POST',
        body: { credential: credentialResponse && credentialResponse.credential }
      });
      setGoogleUser(result.user);

      if (result.enrolled) {
        setEnrollment(null);
        setStep('2fa_verify');
      } else {
        // First sign-in: show the QR code so the user can add the account
        // to their authenticator app.
        setEnrollment(result.enrollment);
        setStep('qr');
      }
    } catch (err) {
      setError(err.message || 'Failed to process Google login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');

    const code = twoFACodeInput.trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setLoading(true);
    try {
      const result = await apiRequest('/api/auth/totp', {
        method: 'POST',
        body: { code, rememberDevice }
      });
      if (onAuthSuccess) onAuthSuccess(result.user);
    } catch (err) {
      if (err.body && err.body.restart) {
        resetToGoogle(err.message);
      } else {
        setError(err.message || 'Verification error. Please try again.');
        setTwoFACodeInput('');
        if (codeInputRef.current) codeInputRef.current.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQrScanned = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setError('');
    setStep('2fa_verify');
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(enrollment.secret);
      setSecretCopied(true);
    } catch (e) {
      setSecretCopied(false);
    }
  };

  if (!googleClientId) {
    return (
      <div className="form-container">
        <h2 className="form-title">Configuration Error</h2>
        <p className="intro-text">
          Google OAuth Client ID is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID via environment variables (for example in .env.local during development).
        </p>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <div className="form-container">
        <div className="brand-mark" aria-hidden="true">DO</div>
        <h2 className="form-title">Sign In</h2>
        <p className="intro-text">Sign in to access the DigitalOcean Billing Dashboard.</p>

        {notice && step === 'google' && !error && (
          <div className="data-source-notice" role="status">{notice}</div>
        )}

        {step === 'google' && (
          <div className="login-google">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme={theme === 'dark' ? 'filled_black' : 'outline'}
            />
            {loading && (
              <p role="status"><span className="loading-spinner" aria-hidden="true"></span>Processing Google login...</p>
            )}
          </div>
        )}

        {step === 'qr' && googleUser && enrollment && (
          <form onSubmit={handleQrScanned}>
            <UserSummary user={googleUser} />

            <div className="login-panel">
              <strong>Set Up Two-Factor Authentication</strong>
              <p>
                Scan this QR code with Google Authenticator, Microsoft Authenticator, or Authy.
              </p>
            </div>

            <div className="login-qr">
              <img src={enrollment.qrCodeDataUrl} alt="QR code for your authenticator app" width="200" height="200" />
              <p className="login-qr-hint">Can't scan? Enter this code manually:</p>
              <code className="login-secret">{enrollment.secret}</code>
              <div>
                <button type="button" className="btn-secondary btn-small" onClick={handleCopySecret}>
                  {secretCopied ? 'Copied' : 'Copy code'}
                </button>
              </div>
            </div>

            <div className="form-buttons form-buttons-stacked">
              <button type="submit">I've Scanned the QR Code</button>
              <button type="button" className="btn-secondary" onClick={() => resetToGoogle()}>Back</button>
            </div>
          </form>
        )}

        {step === '2fa_verify' && googleUser && (
          <form onSubmit={handleVerifyCode} noValidate>
            <UserSummary user={googleUser} />

            <div className="login-panel">
              <strong>Verify Your Authenticator</strong>
              <p>Enter the 6-digit code from your authenticator app.</p>
            </div>

            <div className="form-group">
              <label htmlFor="totpCode">6-digit Code</label>
              <input
                id="totpCode"
                ref={codeInputRef}
                className="login-code-input"
                value={twoFACodeInput}
                onChange={(e) => setTwoFACodeInput(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={error ? 'login-error' : undefined}
                required
              />
            </div>

            <div className="form-group checkbox-row">
              <input
                type="checkbox"
                id="rememberDevice"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
              <label htmlFor="rememberDevice">Remember this device for 30 days</label>
            </div>

            <div className="form-buttons form-buttons-split">
              <button type="submit" disabled={loading}>{loading ? 'Verifying...' : 'Verify Code'}</button>
              {enrollment ? (
                <button type="button" className="btn-secondary" onClick={() => { setError(''); setStep('qr'); }}>Show QR Code</button>
              ) : (
                <button type="button" className="btn-secondary" onClick={() => resetToGoogle()}>Back</button>
              )}
            </div>
          </form>
        )}

        {error && (
          <div className="alert" id="login-error" role="alert" style={{ marginTop: '16px' }}>
            <span>{error}</span>
            <button type="button" className="close-btn" onClick={() => setError('')} aria-label="Dismiss error">×</button>
          </div>
        )}

        <div className="login-footnote">
          <p>Your account is protected by Google OAuth 2.0 and Time-based One-Time Password (TOTP) authentication using your authenticator app.</p>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};
